import React, { useState } from 'react';
import { Button, Select, Input, Table, Modal, Tabs, DatePicker, Space, Card, Alert, Tag, Row, Col } from 'antd';
import { PlusOutlined, SearchOutlined, DownloadOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';

import '../project-management.scss';
import './style-guide.scss';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const StyleGuide = () => {
    const [modalVisible, setModalVisible] = useState(false);
    const [financialModalVisible, setFinancialModalVisible] = useState(false);

    // Sample data for demonstrations
    const sampleTableData = [
        {
            key: '1',
            client: 'Sample Client A',
            name: 'Project Alpha',
            status: 'On-Going',
            pic: 'John Doe',
            totalCostUsd: '$45,000',
            totalProfit: '$15,000'
        },
        {
            key: '2',
            client: 'Sample Client B',
            name: 'Project Beta',
            status: 'In-Planning',
            pic: 'Jane Smith',
            totalCostUsd: '$30,000',
            totalProfit: '$10,000'
        }
    ];

    const columns = [
        { title: 'Client', dataIndex: 'client', key: 'client' },
        { title: 'Project Name', dataIndex: 'name', key: 'name' },
        { 
            title: 'Status', 
            dataIndex: 'status', 
            key: 'status',
            render: (status) => (
                <div className="status-indicator-wrapper">
                    <span className={`status-dot ${status.toLowerCase().replace(' ', '-')}`} />
                    {status}
                </div>
            )
        },
        { title: 'PIC', dataIndex: 'pic', key: 'pic' },
        { title: 'Total Cost', dataIndex: 'totalCostUsd', key: 'totalCostUsd' },
        { title: 'Total Profit', dataIndex: 'totalProfit', key: 'totalProfit' },
    ];

    return (
        <div className="style-guide-container">
            <div className="style-guide-header">
                <h1>Project Management Style Guide</h1>
                <p>Complete catalog of all components used in the Project Management module</p>
            </div>

            {/* Inconsistencies Alert */}
            <Alert
                message="Current Inconsistencies Found"
                description={
                    <div>
                        <p><strong>Critical Issues:</strong></p>
                        <ul>
                            <li>❌ Input heights vary: 32px (search), 38px (dropdowns), 42px (some modals)</li>
                            <li>❌ Button heights inconsistent: 32px (ADD buttons) vs 38px (retainer buttons)</li>
                            <li>❌ Multiple status indicator implementations with different colors</li>
                            <li>❌ Modal styles duplicated across components instead of shared</li>
                        </ul>
                        <p><strong>Recommendations:</strong></p>
                        <ul>
                            <li>✅ Standardize all interactive elements to 38px height</li>
                            <li>✅ Extract common modal styles to shared component</li>
                            <li>✅ Create single StatusIndicator component</li>
                            <li>✅ Move local styles to global where reusable</li>
                        </ul>
                    </div>
                }
                type="warning"
                showIcon
                className="inconsistencies-alert"
            />

            <Tabs defaultActiveKey="buttons" className="style-guide-tabs">
                {/* BUTTONS TAB */}
                <TabPane tab="Buttons" key="buttons">
                    <div className="component-section">
                        <h2>1. Action Buttons</h2>
                        
                        <div className="component-group">
                            <h3>Primary Actions</h3>
                            <Space size="large" wrap>
                                <div className="component-item">
                                    <Button className="btn-white-add" icon={<PlusOutlined />}>
                                        ADD CLIENT
                                    </Button>
                                    <span className="component-label">
                                        .btn-white-add (Global) - 32px height ⚠️
                                    </span>
                                </div>
                                
                                <div className="component-item">
                                    <Button className="btn-recruit btn-primary" type="primary">
                                        SAVE
                                    </Button>
                                    <span className="component-label">
                                        .btn-recruit.btn-primary (Global)
                                    </span>
                                </div>
                            </Space>
                        </div>

                        <div className="component-group">
                            <h3>Toggle Buttons</h3>
                            <Space size="large" wrap>
                                <div className="component-item">
                                    <Space>
                                        <Button className="retainer-btn">ALL</Button>
                                        <Button className="retainer-btn active">RETAINER</Button>
                                        <Button className="retainer-btn">NON-RETAINER</Button>
                                    </Space>
                                    <span className="component-label">
                                        .retainer-btn (Local) - 38px height ⚠️
                                    </span>
                                </div>

                                <div className="component-item">
                                    <div className="currency-toggle">
                                        <Button className="currency-btn">USD</Button>
                                        <Button className="currency-btn active">CNY</Button>
                                    </div>
                                    <span className="component-label">
                                        .currency-btn (Local)
                                    </span>
                                </div>
                            </Space>
                        </div>

                        <div className="component-group">
                            <h3>Icon Buttons</h3>
                            <Space size="large" wrap>
                                <Button className="btn-circle-edit" icon={<EditOutlined />} />
                                <Button className="btn-circle-delete" icon={<DeleteOutlined />} />
                                <Button className="btn-circle-download" icon={<DownloadOutlined />} />
                                <span className="component-label">
                                    .btn-circle-* (Global)
                                </span>
                            </Space>
                        </div>
                    </div>
                </TabPane>

                {/* DROPDOWNS TAB */}
                <TabPane tab="Dropdowns & Inputs" key="dropdowns">
                    <div className="component-section">
                        <h2>2. Dropdowns & Select Components</h2>
                        
                        <div className="component-group">
                            <h3>Filter Dropdowns</h3>
                            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                                <div className="component-item">
                                    <Select
                                        className="yellow-dropdown"
                                        style={{ width: 200 }}
                                        placeholder="ALL DEPARTMENTS"
                                    >
                                        <Option value="all">ALL DEPARTMENTS</Option>
                                        <Option value="tech">Technology</Option>
                                        <Option value="marketing">Marketing</Option>
                                    </Select>
                                    <span className="component-label">
                                        .yellow-dropdown (Global) - 38px height ✅
                                    </span>
                                </div>

                                <div className="component-item">
                                    <Select
                                        mode="multiple"
                                        className="yellow-dropdown"
                                        style={{ width: 250 }}
                                        placeholder="ALL STATUS"
                                    >
                                        <Option value="ongoing">On-Going</Option>
                                        <Option value="planning">In-Planning</Option>
                                        <Option value="completed">Completed</Option>
                                    </Select>
                                    <span className="component-label">
                                        Multi-select with .yellow-dropdown
                                    </span>
                                </div>
                            </Space>
                        </div>

                        <div className="component-group">
                            <h3>Form Inputs</h3>
                            <Space direction="vertical" size="large" style={{ width: '100%', maxWidth: 400 }}>
                                <div className="component-item">
                                    <Input 
                                        placeholder="Search projects..."
                                        prefix={<SearchOutlined />}
                                        allowClear
                                    />
                                    <span className="component-label">
                                        Search Input (Local) - 32px height ⚠️
                                    </span>
                                </div>

                                <div className="component-item">
                                    <Input 
                                        placeholder="Modal form input"
                                        className="modal-input"
                                        style={{ height: 42 }}
                                    />
                                    <span className="component-label">
                                        Modal Input - 42px height ⚠️
                                    </span>
                                </div>

                                <div className="component-item">
                                    <RangePicker />
                                    <span className="component-label">
                                        Date Range Picker (Ant Design default)
                                    </span>
                                </div>
                            </Space>
                        </div>
                    </div>
                </TabPane>

                {/* MODALS TAB */}
                <TabPane tab="Modals" key="modals">
                    <div className="component-section">
                        <h2>3. Modal Components</h2>
                        
                        <div className="component-group">
                            <h3>Business Logic Modals</h3>
                            <Space size="large" wrap>
                                <Button onClick={() => setModalVisible(true)}>
                                    Open Client Modal
                                </Button>
                                <Button onClick={() => setModalVisible(true)}>
                                    Open Vendor Modal
                                </Button>
                                <Button onClick={() => setModalVisible(true)}>
                                    Open Contact Modal
                                </Button>
                            </Space>
                            <p className="component-note">
                                Each modal has its own stylesheet (ClientModal.scss, VendorModal.scss, etc.) - Should be unified!
                            </p>
                        </div>

                        <div className="component-group">
                            <h3>Financial Modals</h3>
                            <Space size="large" wrap>
                                <Button onClick={() => setFinancialModalVisible(true)}>
                                    Open Total Cost Modal
                                </Button>
                                <Button onClick={() => setFinancialModalVisible(true)}>
                                    Open Total Profit Modal
                                </Button>
                                <Button onClick={() => setFinancialModalVisible(true)}>
                                    Open Formula Modal
                                </Button>
                            </Space>
                            <p className="component-note">
                                Financial modals share styles.scss but have unique calculation displays
                            </p>
                        </div>

                        {/* Sample Modal */}
                        <Modal
                            title="Sample Modal"
                            visible={modalVisible}
                            onOk={() => setModalVisible(false)}
                            onCancel={() => setModalVisible(false)}
                            width={600}
                            className="pm-modal"
                        >
                            <div className="modal-content-sample">
                                <p>This is a sample modal showing the .pm-modal wrapper class</p>
                                <div style={{ marginTop: 16 }}>
                                    <Input placeholder="Input height varies by modal" />
                                </div>
                            </div>
                        </Modal>

                        {/* Financial Modal Sample */}
                        <Modal
                            title="Total Cost Breakdown"
                            visible={financialModalVisible}
                            onCancel={() => setFinancialModalVisible(false)}
                            footer={null}
                            width={500}
                            className="financial-modal"
                        >
                            <div className="financial-modal-content">
                                <div className="main-value cost">
                                    $125,450.00
                                </div>
                                <div className="currency-note">
                                    All values converted to USD at project creation date
                                </div>
                                <div className="cost-calculation">
                                    <div className="calc-row">
                                        <span className="label">Manhour Cost</span>
                                        <span className="value">$45,000.00</span>
                                    </div>
                                    <div className="calc-row operator">+</div>
                                    <div className="calc-row">
                                        <span className="label">Vendor Cost</span>
                                        <span className="value">$80,450.00</span>
                                    </div>
                                    <div className="calc-row total">
                                        <span className="label">Total Cost</span>
                                        <span className="value">$125,450.00</span>
                                    </div>
                                </div>
                            </div>
                        </Modal>
                    </div>
                </TabPane>

                {/* TABLES TAB */}
                <TabPane tab="Tables" key="tables">
                    <div className="component-section">
                        <h2>4. Table Components</h2>
                        
                        <div className="component-group">
                            <h3>Projects Table</h3>
                            <Table 
                                dataSource={sampleTableData}
                                columns={columns}
                                className="data-table"
                                pagination={{
                                    pageSize: 5,
                                    showSizeChanger: false
                                }}
                            />
                            <p className="component-note">
                                .data-table (Local) - Custom header bg: rgb(222, 226, 230)
                            </p>
                        </div>
                    </div>
                </TabPane>

                {/* STATUS INDICATORS TAB */}
                <TabPane tab="Status Indicators" key="status">
                    <div className="component-section">
                        <h2>5. Status Indicators</h2>
                        
                        <div className="component-group">
                            <h3>Status Dots</h3>
                            <Space direction="vertical" size="large">
                                <div className="status-indicator-wrapper">
                                    <span className="status-dot on-going" />
                                    On-Going
                                    <span className="component-label">Green #52c41a</span>
                                </div>
                                <div className="status-indicator-wrapper">
                                    <span className="status-dot in-planning" />
                                    In-Planning
                                    <span className="component-label">Yellow #faad14</span>
                                </div>
                                <div className="status-indicator-wrapper">
                                    <span className="status-dot on-hold" />
                                    On-Hold
                                    <span className="component-label">Red #ff4d4f</span>
                                </div>
                                <div className="status-indicator-wrapper">
                                    <span className="status-dot completed" />
                                    Completed
                                    <span className="component-label">Green #52c41a</span>
                                </div>
                            </Space>
                        </div>

                        <div className="component-group">
                            <h3>Status Badges</h3>
                            <Space size="large" wrap>
                                <Tag color="success">Active</Tag>
                                <Tag color="processing">In Progress</Tag>
                                <Tag color="warning">Pending</Tag>
                                <Tag color="error">Inactive</Tag>
                            </Space>
                            <p className="component-note">
                                Multiple implementations exist - needs standardization
                            </p>
                        </div>
                    </div>
                </TabPane>

                {/* LAYOUTS TAB */}
                <TabPane tab="Layouts" key="layouts">
                    <div className="component-section">
                        <h2>6. Layout Components</h2>
                        
                        <div className="component-group">
                            <h3>Page Container</h3>
                            <div className="demo-container project-management-container">
                                <div className="section-header">
                                    <div className="header-left">
                                        <h4>SECTION TITLE</h4>
                                        <span className="count-badge">42</span>
                                    </div>
                                    <div className="header-right">
                                        <Button className="btn-white-add" icon={<PlusOutlined />}>
                                            ADD ITEM
                                        </Button>
                                    </div>
                                </div>
                                <p className="component-note">
                                    .project-management-container with .section-header (Local)
                                </p>
                            </div>
                        </div>

                        <div className="component-group">
                            <h3>Control Row</h3>
                            <div className="controls-row">
                                <Input 
                                    placeholder="Search..."
                                    prefix={<SearchOutlined />}
                                    style={{ width: 300 }}
                                />
                                <Select className="yellow-dropdown" style={{ width: 150 }}>
                                    <Option value="all">All Status</Option>
                                </Select>
                                <RangePicker />
                            </div>
                            <p className="component-note">
                                .controls-row (Local) - Flex container with 12px gap
                            </p>
                        </div>

                        <div className="component-group">
                            <h3>Detail Page Cards</h3>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Card className="stat-card">
                                        <div className="stat-label">Total Revenue</div>
                                        <div className="stat-value">$245,000</div>
                                    </Card>
                                </Col>
                                <Col span={8}>
                                    <Card className="stat-card">
                                        <div className="stat-label">Total Cost</div>
                                        <div className="stat-value cost">$180,000</div>
                                    </Card>
                                </Col>
                                <Col span={8}>
                                    <Card className="stat-card">
                                        <div className="stat-label">Net Profit</div>
                                        <div className="stat-value profit">$65,000</div>
                                    </Card>
                                </Col>
                            </Row>
                            <p className="component-note">
                                .stat-card (Local) - Used in detail pages
                            </p>
                        </div>
                    </div>
                </TabPane>

                {/* TYPOGRAPHY TAB */}
                <TabPane tab="Typography & Colors" key="typography">
                    <div className="component-section">
                        <h2>7. Typography & Colors</h2>
                        
                        <div className="component-group">
                            <h3>Color Palette</h3>
                            <div className="color-grid">
                                <div className="color-item">
                                    <div className="color-box" style={{ backgroundColor: '#FED230' }} />
                                    <span>Primary Yellow<br />#FED230</span>
                                </div>
                                <div className="color-item">
                                    <div className="color-box" style={{ backgroundColor: '#FEC578' }} />
                                    <span>Secondary Yellow<br />#FEC578</span>
                                </div>
                                <div className="color-item">
                                    <div className="color-box" style={{ backgroundColor: '#1B1B1B' }} />
                                    <span>Black<br />#1B1B1B</span>
                                </div>
                                <div className="color-item">
                                    <div className="color-box" style={{ backgroundColor: '#52c41a' }} />
                                    <span>Success Green<br />#52c41a</span>
                                </div>
                                <div className="color-item">
                                    <div className="color-box" style={{ backgroundColor: '#ff4d4f' }} />
                                    <span>Error Red<br />#ff4d4f</span>
                                </div>
                                <div className="color-item">
                                    <div className="color-box" style={{ backgroundColor: '#faad14' }} />
                                    <span>Warning Yellow<br />#faad14</span>
                                </div>
                            </div>
                        </div>

                        <div className="component-group">
                            <h3>Typography Scale</h3>
                            <Space direction="vertical" size="large">
                                <div style={{ fontSize: 18, fontWeight: 600 }}>
                                    Page Title - 18px/600 Avenir
                                </div>
                                <div style={{ fontSize: 16, fontWeight: 600 }}>
                                    Section Header - 16px/600 Avenir
                                </div>
                                <div style={{ fontSize: 14, fontWeight: 500 }}>
                                    Subsection - 14px/500 Avenir
                                </div>
                                <div style={{ fontSize: 14, fontWeight: 400 }}>
                                    Body Text - 14px/400 Avenir
                                </div>
                                <div style={{ fontSize: 11, fontWeight: 400, textTransform: 'uppercase' }}>
                                    BUTTON TEXT - 11px/400 UPPERCASE
                                </div>
                            </Space>
                        </div>
                    </div>
                </TabPane>
            </Tabs>

            {/* Summary Section */}
            <div className="style-guide-summary">
                <h2>Summary & Recommendations</h2>
                <Row gutter={24}>
                    <Col span={8}>
                        <Card title="Global Styles ✅" className="summary-card">
                            <ul>
                                <li>.btn-white-add (buttons)</li>
                                <li>.btn-recruit.btn-primary</li>
                                <li>.yellow-dropdown</li>
                                <li>.btn-circle-* (icon buttons)</li>
                                <li>Base Ant Design components</li>
                            </ul>
                        </Card>
                    </Col>
                    <Col span={8}>
                        <Card title="Should Be Global ⚠️" className="summary-card warning">
                            <ul>
                                <li>.retainer-btn (toggle buttons)</li>
                                <li>.currency-btn (currency toggle)</li>
                                <li>.status-indicator-wrapper</li>
                                <li>.data-table (table styles)</li>
                                <li>.pm-modal (modal wrapper)</li>
                                <li>.stat-card (metric cards)</li>
                            </ul>
                        </Card>
                    </Col>
                    <Col span={8}>
                        <Card title="Needs Refactoring 🔴" className="summary-card error">
                            <ul>
                                <li>Standardize heights to 38px</li>
                                <li>Extract modal shared styles</li>
                                <li>Unify status indicators</li>
                                <li>Create color variables</li>
                                <li>Consolidate duplicate styles</li>
                                <li>Remove inline styles</li>
                            </ul>
                        </Card>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default StyleGuide;